import axiosInstance from '@/utils/axios';
import { API_BASE_URL } from '@/config/api.config';

const API_URL = API_BASE_URL;

export interface Exam {
  exam_id: string;
  name: string;
  name_en?: string;
  certifying_body: string;
  exam_status: 'active' | 'legacy' | 'inactive' | 'upcoming';
  total_questions: number;
  duration_minutes: number;
  passing_score_percent: number;
  calculator_required: boolean;
  calculator_type?: string;
  language: string;
  validity_start_date?: string;
  validity_end_date?: string;
  registration_url?: string;
  created_at: string;
  updated_at: string;
  question_count?: number;
  topics_count?: number;
}

export interface ExamFilters {
  status?: string;
  certifying_body?: string;
  search?: string;
}

class ExamsService {
  /**
   * Get all exams with optional filters
   */
  async getExams(filters?: ExamFilters): Promise<Exam[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.certifying_body) params.append('certifying_body', filters.certifying_body);
    if (filters?.search) params.append('search', filters.search);

    const response = await axiosInstance.get(`${API_URL}/exams?${params.toString()}`);
    return response.data.data || [];
  }

  /**
   * Get exam by ID
   */
  async getExamById(examId: string): Promise<Exam | null> {
    const response = await axiosInstance.get(`${API_URL}/exams/${examId}`);
    return response.data.data;
  }

  /**
   * Create new exam
   */
  async createExam(examData: Partial<Exam>): Promise<Exam> {
    const response = await axiosInstance.post(`${API_URL}/exams`, examData);
    return response.data.data;
  }

  /**
   * Update exam
   */
  async updateExam(examId: string, examData: Partial<Exam>): Promise<Exam> {
    const response = await axiosInstance.put(`${API_URL}/exams/${examId}`, examData);
    return response.data.data;
  }

  /**
   * Delete exam
   */
  async deleteExam(examId: string): Promise<void> {
    await axiosInstance.delete(`${API_URL}/exams/${examId}`);
  }

  /**
   * Get exam mappings (already exists in backend via content service)
   */
  async getExamMappings(examId: string): Promise<any[]> {
    try {
      const response = await axiosInstance.get(`${API_URL}/exams/${examId}/mappings`);
      return response.data.data || [];
    } catch (error) {
      console.error('Failed to get exam mappings:', error);
      return [];
    }
  }
}

export const examsService = new ExamsService();
